import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { fetchMessages, isAuthenticated, getToken, clearToken } from "./api";
import Sidebar from "./components/Sidebar";
import ChatWindow from "./components/ChatWindow";
import Login from "./components/Login";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_BASE_URL?.replace("/api", "") ||
  "http://localhost:5500";

// WA-P0-01: don't auto-connect -- the socket only connects once a staff session exists
// (see the `authed` effect below), and carries the staff token in the handshake so the
// server's io.use(...) auth check (server.js) can reject unauthenticated socket clients too.
const socket = io(SOCKET_URL, {
  transports: ["websocket"],
  reconnectionAttempts: 5,
  reconnectionDelay: 2000,
  autoConnect: false,
});

function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [messages, setMessages] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("active");

  // WA-P0-01: no conversation data is fetched until a staff session exists. If the session
  // expires later (401 from the backend), fall back to the login screen instead of leaving
  // stale data on screen.
  const loadMessages = async () => {
    setLoadingChats(true);
    try {
      const data = await fetchMessages();
      setMessages(data);
    } catch (err) {
      if (err.status === 401) {
        setAuthed(false);
      } else {
        console.error("❌ Error fetching messages:", err);
      }
    } finally {
      setLoadingChats(false);
    }
  };

  // Load all messages once authenticated
  useEffect(() => {
    if (!authed) return;
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // WA-P0-01: connect/disconnect the socket alongside auth state, carrying the staff token.
  useEffect(() => {
    if (!authed) {
      socket.disconnect();
      return;
    }
    socket.auth = { token: getToken() };
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, [authed]);

  // Setup Socket.io (handles real-time events for all message creation paths: the Meta webhook
  // handler and every send action emit "newMessage"/"messageStatusUpdate" from the same backend
  // process, see fixspire-service-whatsapp's webhookRoutes.js/messageController.js). Polling was
  // removed (WA-P1-xx): it was only ever needed for the local-dev case of a developer's UI pointed
  // at localhost while Meta's webhook can only reach the public deployed URL -- in any real
  // single-instance deployment the socket already delivers every message in real time. A one-shot
  // refetch on socket *reconnect* (not a timer) reconciles anything missed during a connection gap.
  useEffect(() => {
    socket.on("connect", () => {
      console.log("🟢 Connected to Socket.io server");
      setIsConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("🔴 Disconnected from Socket.io");
      setIsConnected(false);
    });

    socket.on("newMessage", (msg) => {
      console.log("📩 New message received via socket:", msg);
      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev;

        // WA-06: this message comes straight from the webhook/send handlers, not through
        // GET /api/messages, so it doesn't carry the server-computed conversationStatus yet
        // (that's only attached at fetch time). Left as-is, it would default to "inactive" in
        // Sidebar.js -- for an inbound message that's actively wrong (a
        // brand new customer message must open/reset the window immediately), and for an
        // outbound message it would incorrectly flip an active conversation to inactive for a
        // few seconds. An inbound message always makes the conversation active-from-now; an
        // outbound message never changes the window, so it inherits whatever status the
        // conversation already had.
        const priorForPhone = prev.find((m) => m.phone_number === msg.phone_number);
        const enrichedMsg =
          msg.direction === "inbound"
            ? { ...msg, conversationStatus: "active", conversationLastInboundAt: msg.timestamp }
            : {
                ...msg,
                conversationStatus: priorForPhone?.conversationStatus || "inactive",
                conversationLastInboundAt: priorForPhone?.conversationLastInboundAt || null,
              };

        return [enrichedMsg, ...prev];
      });
    });

    socket.on("messageStatusUpdate", (updatedMsg) => {
      console.log("📦 Status update:", updatedMsg);
      setMessages((prev) =>
        prev.map((m) =>
          m._id === updatedMsg._id ? { ...m, status: updatedMsg.status } : m
        )
      );
    });

    // Reconciliation, not polling: fires only on an actual reconnect (after a real disconnect),
    // never on a timer, so it can't repeatedly hit the rate limiter -- it just catches up on
    // anything that arrived while the socket was down.
    const handleReconnect = () => {
      console.log("🔁 Socket reconnected -- reconciling message list");
      loadMessages();
    };
    socket.io.on("reconnect", handleReconnect);

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("newMessage");
      socket.off("messageStatusUpdate");
      socket.io.off("reconnect", handleReconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // Get unique chats (one per phone number) and filter by search
  const uniqueChats = messages
    .reduce((unique, msg) => {
      if (!msg.phone_number) return unique; // skip messages with no phone_number
      if (!unique.find((u) => u.phone_number === msg.phone_number)) {
        unique.push(msg);
      }
      return unique;
    }, [])
    .filter((chat) => {
      const q = (debouncedSearch || "").trim().toLowerCase();
      if (!q) return true;
      const phone = (chat.phone_number || "").toLowerCase();
      const text = (chat.message_text || "").toLowerCase();
      const name = (chat.contact_name || chat.name || "").toLowerCase();
      return phone.includes(q) || text.includes(q) || name.includes(q);
    });

  // WA-P0-01: no conversation data (Sidebar/ChatWindow) renders without a valid session.
  if (!authed) {
    return <Login onLoginSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="flex h-screen w-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans">
      {/* Sidebar */}
      <div className={`${selectedPhone ? "hidden" : "block"} sm:block w-full sm:w-80 sm:flex-shrink-0 sm:border-r border-gray-200`}>
        <Sidebar
          uniqueChats={uniqueChats}
          selectedPhone={selectedPhone}
          onSelectChat={setSelectedPhone}
          isConnected={isConnected}
          loadingChats={loadingChats}
          searchText={searchText}
          setSearchText={setSearchText}
          highlightText={debouncedSearch}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      {/* ChatWindow */}
      <div className={`${selectedPhone ? "flex" : "hidden sm:flex"} flex-1 w-full`}>
        <ChatWindow
          selectedPhone={selectedPhone}
          messages={messages}
          socket={socket}
          onBack={() => setSelectedPhone(null)}
        />
      </div>

      {/* WA-P0-01: minimal sign-out control */}
      <button
        onClick={() => {
          clearToken();
          setAuthed(false);
        }}
        className="fixed top-2 right-2 z-50 text-xs bg-white/90 hover:bg-white text-gray-600 border border-gray-200 rounded-md px-2 py-1 shadow-sm"
      >
        Sign out
      </button>
    </div>
  );
}

export default App;